import { createContext, PropsWithChildren, ReactElement, useContext, useMemo } from 'react';
import { useSessionContext } from '~/contexts/session.context.tsx';
import { useGetRoleTypesQuery } from '~/services/options.ts';
import { UserRole } from '~/services/types';
import { Navigate } from 'react-router-dom';
import { getRoutePath } from '~/router/routes.ts';

export enum UserRolePermission {
  Owner = 1 << 0, // 1
  User = 1 << 1, // 2
  Coach = 1 << 2, // 4
  Manager = 1 << 3, // 16
  Staff = 1 << 4, // 32
  Member = 1 << 5, // 64
  // This is a fake role, but since we query for Owner or Manager in lots of places, we need to have a way to check for both roles at the same time
  OwnerOrManager = 1 << 20, // 1048576

  // Virtual role for DEA users
  DeaUser = 1 << 21, // 2097152

  // Virtual role for NoDEA users
  NoDeaUser = 1 << 22, // 4194304
}

export type ContextProps = {
  role: UserRole | null;
  mappedRole: UserRolePermission | null;
  isOwnerOrManager: boolean;
  isContextReady: boolean;
  checkRoles: (rolesToCheck: UserRolePermission) => boolean;
  renderIfGranted: (roles: number, render: () => ReactElement) => ReactElement | null;
  userRoleIds: Record<number, number>;
};

type ProviderProps = PropsWithChildren;

const hasRole = (userRoles: UserRolePermission, requiredRoles: number): boolean => {
  return (userRoles & requiredRoles) !== 0;
};

export const getUserRoleById = (UserRoleIds: ContextProps['userRoleIds'], roleId: number): UserRolePermission => {
  const roleEntry = Object.entries(UserRoleIds).find(([_, id]) => id === roleId);

  // Return the corresponding UserRolePermission or null if no match is found
  if (!roleEntry) {
    throw new Error(
      `Role not found. There is a mismatch between the enum UserRole and roles from json file or roles from API - role_id: ${roleId}`,
    );
  }

  return parseInt(roleEntry[0], 10) as UserRolePermission;
};

const defaultContext = {
  role: null,
  mappedRole: null,
  isOwnerOrManager: false,
  isContextReady: false,
  renderIfGranted: () => null,
  checkRoles: () => false,
  userRoleIds: {},
};

const RoleAuthenticationContext = createContext<ContextProps>(defaultContext);

const RoleAuthenticationProvider = ({ children }: ProviderProps) => {
  const sessionContext = useSessionContext();
  const { data: roles, isLoading: isRoleLoading } = useGetRoleTypesQuery();

  const { currentUser, isLoading } = sessionContext;

  const userRoleIds = (roles || []).reduce<Record<number, number>>((acc, role) => {
    acc[UserRolePermission[role.name as keyof typeof UserRolePermission]] = role.id;

    return acc;
  }, {});

  const isContextReady = !isLoading && !isRoleLoading;

  const providerValue = useMemo(() => {
    if (!isContextReady || !currentUser) {
      return defaultContext;
    }

    const mappedUserRoles = getUserRoleById(userRoleIds, currentUser.role.id);
    const checkRoles = (rolesToCheck: UserRolePermission) => {
      // Handle this special case since this is not a role by its own, but it's common to check for both roles at the same time
      if (rolesToCheck === UserRolePermission.OwnerOrManager) {
        return hasRole(mappedUserRoles, UserRolePermission.Owner | UserRolePermission.Manager);
      }

      // Handle DEA virtual role
      if (rolesToCheck === UserRolePermission.DeaUser) {
        return currentUser.locations.some((location) => location.is_dea);
      }

      // Handle NoDEA virtual role
      if (rolesToCheck === UserRolePermission.NoDeaUser) {
        return !currentUser.locations.some((location) => location.is_dea);
      }

      return hasRole(mappedUserRoles, rolesToCheck);
    };

    return {
      role: currentUser.role,
      mappedRole: mappedUserRoles, // this is the role from UserRole enum which uses bitwise operators to check for roles
      isOwnerOrManager: checkRoles(UserRolePermission.Owner | UserRolePermission.Manager),
      checkRoles,
      renderIfGranted: (roles: number, render: () => ReactElement) => (checkRoles(roles) ? render() : null),
      userRoleIds,
      isContextReady,
    };
  }, [userRoleIds, currentUser, isContextReady]);

  return <RoleAuthenticationContext.Provider value={providerValue}>{children}</RoleAuthenticationContext.Provider>;
};

const ProtectedRoute = ({ roles, children }: { roles: number; children: ReactElement }) => {
  const { checkRoles, isContextReady } = useRoleAuthenticationContext();

  if (!isContextReady) return null;

  if (checkRoles(roles)) {
    return children;
  }

  return <Navigate to={getRoutePath('root')} replace />;
};

const useRoleAuthenticationContext = () => {
  const context = useContext(RoleAuthenticationContext);

  if (!context) {
    throw new Error('useRoleAuthenticationContext must be used within a RoleAuthenticationProvider');
  }

  return context;
};

export { RoleAuthenticationProvider, useRoleAuthenticationContext, ProtectedRoute };
